import api from './api';
import { clearFavoritesCache } from '../utils/favorites';
import { isTransientApiError } from '../utils/apiErrors';
import {
  notifyTokensCleared,
  notifyTokensUpdated,
  readTokensFromStorage,
  releaseRefreshLock,
  tryAcquireRefreshLock,
  waitForTokenUpdateFromOtherTab,
} from './authTokenSync';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginSuccessResponse {
  access_token: string;
  refresh_token: string;
  session_uuid?: string;
}

export interface Login2faRequiredResponse {
  requires2fa: true;
  pendingToken: string;
}

export type LoginResponse = LoginSuccessResponse | Login2faRequiredResponse;

export interface SessionInfo {
  session_uuid: string;
  created_at: string;
  expires_at: string;
  user_agent: string | null;
  ip_address: string | null;
  is_current: boolean;
}

export interface TwoFactorSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

export type RefreshAccessTokenResult =
  | { status: 'success'; data: LoginSuccessResponse }
  | { status: 'transient_failure' }
  | { status: 'auth_failure' };

const REFRESH_TOKEN_KEY = 'refresh_token';

let inFlightRefresh: Promise<RefreshAccessTokenResult> | null = null;

function storeTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem('access_token', accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  notifyTokensUpdated();
}

function wipeTokens(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  notifyTokensCleared();
}

async function performRefreshAccessToken(): Promise<RefreshAccessTokenResult> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return { status: 'auth_failure' };
  }

  if (!tryAcquireRefreshLock()) {
    const synced = await waitForTokenUpdateFromOtherTab(refreshToken);
    if (synced) {
      const tokens = readTokensFromStorage();
      if (tokens) {
        return { status: 'success', data: tokens };
      }
    }
    if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
      return { status: 'auth_failure' };
    }
    return { status: 'transient_failure' };
  }

  try {
    const response = await api.post<LoginSuccessResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    storeTokens(response.data.access_token, response.data.refresh_token);
    return { status: 'success', data: response.data };
  } catch (error) {
    if (isTransientApiError(error)) {
      return { status: 'transient_failure' };
    }
    if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
      wipeTokens();
    }
    return { status: 'auth_failure' };
  } finally {
    releaseRefreshLock();
  }
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  async verify2fa(pendingToken: string, code?: string, recoveryCode?: string): Promise<LoginSuccessResponse> {
    const response = await api.post<LoginSuccessResponse>('/auth/2fa/verify', {
      pendingToken,
      code,
      recoveryCode,
    });
    return response.data;
  },

  async refreshAccessToken(): Promise<RefreshAccessTokenResult> {
    if (inFlightRefresh) return inFlightRefresh;
    inFlightRefresh = performRefreshAccessToken().finally(() => {
      inFlightRefresh = null;
    });
    return inFlightRefresh;
  },

  async logout(): Promise<void> {
    await api.delete('/auth/logout');
    this.clearTokens();
    clearFavoritesCache();
  },

  async validateSession(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/validate-session');
    return response.data;
  },

  async pingSession(): Promise<void> {
    await api.post('/auth/session/ping');
  },

  getToken(): string | null {
    return localStorage.getItem('access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setToken(token: string): void {
    storeTokens(token);
  },

  setTokens(accessToken: string, refreshToken?: string): void {
    storeTokens(accessToken, refreshToken);
  },

  clearTokens(): void {
    wipeTokens();
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  },

  async setup2fa(): Promise<TwoFactorSetupResponse> {
    const response = await api.post<TwoFactorSetupResponse>('/auth/2fa/setup');
    return response.data;
  },

  async enable2fa(code: string): Promise<{ recoveryCodes: string[] }> {
    const response = await api.post<{ recoveryCodes: string[] }>('/auth/2fa/enable', { code });
    return response.data;
  },

  async disable2fa(params: {
    password: string;
    code?: string;
    recoveryCode?: string;
  }): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/auth/2fa/disable', params);
    return response.data;
  },

  async listSessions(): Promise<SessionInfo[]> {
    const response = await api.get<SessionInfo[]>('/auth/sessions');
    return response.data;
  },

  async revokeSession(sessionUuid: string): Promise<void> {
    await api.post('/auth/revoke-session', { session_uuid: sessionUuid });
  },
};
